/**
 * LOADING & ERROR STATE ATOMS
 *
 * Track loading and error states for Firebase subscriptions
 *
 * Key concepts:
 * - "initialLoad" = true until first data received
 * - "isLoading" = false once subscription established (even if data updating)
 * - "error" = error state per document/query
 * - "hasData" = whether we've received data at least once
 */

import { atom } from "jotai"
import { atomFamily } from "jotai/utils"

import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"

interface LoadingState {
  isInitialLoad: boolean // True until first data received
  isUpdating: boolean // True when update in progress (for animations)
  hasData: boolean // True once we've received data at least once
  error: Error | null
  lastUpdateAt: number | null
}

const DEFAULT_LOADING_STATE: LoadingState = {
  isInitialLoad: true,
  isUpdating: false,
  hasData: false,
  error: null,
  lastUpdateAt: null,
}

/**
 * Create loading state atoms for a doc type
 */
export function createLoadingStateAtoms(docType: DOC_TYPES) {
  const logPrefix = `[Loading:${docType}]`

  /**
   * Loading state for individual documents by ID
   */
  const docLoadingStateAtom = atomFamily((id: Uuid | null) => {
    const baseAtom = atom<LoadingState>(DEFAULT_LOADING_STATE)
    baseAtom.debugLabel = `${docType}:loading:${id}`
    return baseAtom
  })

  /**
   * Loading state for batch queries (by key)
   */
  const batchLoadingStateAtom = atomFamily((key: string) => {
    const baseAtom = atom<LoadingState>(DEFAULT_LOADING_STATE)
    baseAtom.debugLabel = `${docType}:batchLoading:${key}`
    return baseAtom
  })

  /**
   * Loading state for filtered queries (by query key)
   */
  const filteredLoadingStateAtom = atomFamily((queryKey: string) => {
    const baseAtom = atom<LoadingState>(DEFAULT_LOADING_STATE)
    baseAtom.debugLabel = `${docType}:filteredLoading:${queryKey}`
    return baseAtom
  })

  /**
   * Loading state for all docs
   */
  const allDocsLoadingStateAtom = atom<LoadingState>(DEFAULT_LOADING_STATE)
  allDocsLoadingStateAtom.debugLabel = `${docType}:allLoading`

  /**
   * Helper: Set loading state (to be called from hooks)
   */
  const setLoadingStateAtom = atom(
    null,
    (
      _get,
      set,
      update: {
        type: "doc" | "batch" | "filtered" | "all"
        key?: string | Uuid
        state: Partial<LoadingState>
      }
    ) => {
      if (update.type === "doc" && update.key) {
        set(docLoadingStateAtom(update.key), (prev) => ({
          ...prev,
          ...update.state,
          lastUpdateAt: Date.now(),
        }))
      } else if (update.type === "batch" && update.key) {
        set(batchLoadingStateAtom(update.key), (prev) => ({
          ...prev,
          ...update.state,
          lastUpdateAt: Date.now(),
        }))
      } else if (update.type === "filtered" && update.key) {
        set(filteredLoadingStateAtom(update.key), (prev) => ({
          ...prev,
          ...update.state,
          lastUpdateAt: Date.now(),
        }))
      } else if (update.type === "all") {
        set(allDocsLoadingStateAtom, (prev) => ({
          ...prev,
          ...update.state,
          lastUpdateAt: Date.now(),
        }))
      }

      // console.log(
      //   `${logPrefix} 🔄 Updated ${update.type} loading state`,
      //   update.state
      // )
    }
  )

  return {
    docLoadingStateAtom,
    batchLoadingStateAtom,
    filteredLoadingStateAtom,
    allDocsLoadingStateAtom,
    setLoadingStateAtom,
    logPrefix,
  }
}

export type LoadingStateAtoms = ReturnType<typeof createLoadingStateAtoms>
export type { LoadingState }
